import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import PanelGroupsContainer from './PanelGroupsContainer';
import Minimap from './components/Minimap';
import { useStartDate } from './StartDateContext';
import { useLoginContext } from './LoginContext';
import { fetchSpecialTimes } from './api';

const CalendarStrips = forwardRef(({ 
  onDataChange, 
  panelGroupsContainerRef,
  visibleWeeks = 10,
  weekWidth = 200,
  isVisibleWeeksChanging = false,
  isZoomChanging = false,
  onInfoPanelsChange,
  batchOperations,
  onCheckForDuplicates
}, ref) => {
  const { startDate } = useStartDate();
  const { user } = useLoginContext();
  const contentRef = useRef(null);
  const calendarStripRef = useRef(null);
  const backgroundRef = useRef(null);
  const [contentHeight, setContentHeight] = useState('100vh');
  const [specialTimes, setSpecialTimes] = useState([]);
  const [minimapGroups, setMinimapGroups] = useState([]);

  // Ref für stabilen Zugriff auf onDataChange ohne Re-Render-Zyklen
  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  // Wrapper für onDataChange: ruft Original-Callback auf und aktualisiert Minimap-Daten
  const handleContainerDataChange = useCallback(() => {
    if (onDataChangeRef.current) {
      onDataChangeRef.current();
    }
    if (panelGroupsContainerRef.current?.getData) {
      const data = panelGroupsContainerRef.current.getData();
      setMinimapGroups(data.groups || []);
    }
  }, [panelGroupsContainerRef]);

  // Funktion zur Berechnung der absoluten x-Koordinaten der Wochenspalten
  const getAbsoluteCalendarXCoordinates = () => {
    if (!calendarStripRef.current) {
      return {
        containerStart: 0,
        containerEnd: 0,
        containerWidth: 0,
        weekColumns: []
      };
    }

    const containerRect = calendarStripRef.current.getBoundingClientRect();
    const weekColumns = [];
    
    // Berechne die Koordinaten für jede Wochenspalte mit fester Breite
    for (let i = 0; i < visibleWeeks; i++) {
      const columnStart = containerRect.left + (i * weekWidth);
      const columnEnd = columnStart + weekWidth;
      
      weekColumns.push({
        weekIndex: i,
        startX: columnStart,
        endX: columnEnd,
        width: weekWidth,
        centerX: columnStart + (weekWidth / 2),
        startDate: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + (i * 7)),
        endDate: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + (i * 7) + 6)
      });
    }

    return {
      containerStart: containerRect.left,
      containerEnd: containerRect.right,
      containerWidth: containerRect.width,
      weekColumns: weekColumns
    };
  };

  // Load special times from API
  useEffect(() => {
    const loadSpecialTimes = async () => {
      try {
        const result = await fetchSpecialTimes(user?.id);
        if (result.error) {
          console.error('Fehler beim Laden der Special Times:', result.error);
        } else {
          setSpecialTimes(result);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Special Times:', error);
      }
    };
    
    loadSpecialTimes();
  }, [user?.id]);

  // Function to check if a date is within a special time period
  const getSpecialTimeForDate = (date) => {
    // Verwende lokales Datum ohne Zeitzonenkonvertierung
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    return specialTimes.find(specialTime => {
      return dateString >= specialTime.start_date && dateString <= specialTime.end_date;
    });
  };

  // Function to get background color for a day based on special times
  const getBackgroundColorForDay = (date, isWeekend, weekIndex) => {
    const specialTime = getSpecialTimeForDate(date);
    
    if (specialTime) {
      // Use special time color
      return specialTime.color;
    }
    
    // Use default colors
    if (isWeekend) {
      return weekIndex % 2 === 0 ? '#D1E7DD' : '#e8f4ea';
    } else {
      return weekIndex % 2 === 0 ? '#E3F2FD' : '#f1f5fe';
    }
  };

  // Function to get header background color for a day based on special times
  const getHeaderBackgroundColorForDay = (date, isWeekend, weekIndex) => {
    const specialTime = getSpecialTimeForDate(date);
    
    if (specialTime) {
      // Darken the special time color for header
      const color = specialTime.color;
      // Simple darkening by reducing opacity or using a darker variant
      if (color === '#FFA07A') { // lachs/salmon
        return '#FF8C69'; // darker salmon
      }
      return color;
    }
    
    // Use default colors
    if (isWeekend) {
      return weekIndex % 2 === 0 ? '#A8D5BA' : '#c8e6c9';
    } else {
      return weekIndex % 2 === 0 ? '#BBDEFB' : '#f1f5fe';
    }
  };

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    getAbsoluteCalendarXCoordinates: () => getAbsoluteCalendarXCoordinates(),
    getWeeksData: () => generateWeeksData()
  }));

  // Kontinuierliche Überwachung der Inhaltshöhe
  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current && backgroundRef.current) {
        // Verwende die tatsächliche scrollHeight des Inhalts
        const contentScrollHeight = contentRef.current.scrollHeight;
        const viewportHeight = window.innerHeight;
        const newHeight = Math.max(contentScrollHeight, viewportHeight);
        
        // Setze die Höhe sowohl für den State als auch direkt für den Background
        setContentHeight(`${newHeight}px`);
        backgroundRef.current.style.height = `${newHeight}px`;
      }
    };

    // Initiale Höhenberechnung
    const initialUpdate = () => {
      setTimeout(updateHeight, 100); // Kurze Verzögerung für DOM-Rendering
    };
    initialUpdate();

    // Kontinuierliche Überwachung mit häufigeren Updates
    const intervalId = setInterval(updateHeight, 500);

    // Observer für DOM-Änderungen
    const observer = new MutationObserver(() => {
      setTimeout(updateHeight, 50);
    });
    
    if (contentRef.current) {
      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    // ResizeObserver für Fenstergrößenänderungen
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(updateHeight, 100);
    });
    
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    // Cleanup
    return () => {
      clearInterval(intervalId);
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [visibleWeeks, weekWidth]);

  // Zusätzlicher Effect für spezielle Fälle (z.B. wenn Panels hinzugefügt/entfernt werden)
  useEffect(() => {
    const updateHeightDelayed = () => {
      setTimeout(() => {
        if (contentRef.current && backgroundRef.current) {
          const contentScrollHeight = contentRef.current.scrollHeight;
          const viewportHeight = window.innerHeight;
          const newHeight = Math.max(contentScrollHeight, viewportHeight, 1000); // Min. 1000px
          
          setContentHeight(`${newHeight}px`);
          backgroundRef.current.style.height = `${newHeight}px`;
        }
      }, 300);
    };

    updateHeightDelayed();
  }, [onDataChange]);

  // Funktion zur Berechnung der Wochendaten
  const generateWeeksData = () => {
    const weeks = [];
    const currentDate = new Date(startDate);
    
    // Zum Montag der ersten Woche navigieren
    const dayOfWeek = currentDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    currentDate.setDate(currentDate.getDate() + mondayOffset);
    currentDate.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < visibleWeeks; i++) {
      const weekStart = new Date(currentDate);
      const weekNumber = getWeekNumber(weekStart);
      const mondayDate = formatDate(weekStart);
      
      weeks.push({
        weekNumber,
        mondayDate,
        fullDate: new Date(weekStart)
      });
      
      // Zur nächsten Woche
      currentDate.setDate(currentDate.getDate() + 7);
    }
    
    return weeks;
  };

  // Funktion zur Berechnung der Kalenderwoche
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Funktion zur Formatierung des Datums
  const formatDate = (date) => {
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const weeksData = generateWeeksData();

  return (
    <div
      id="plan-export-root"
      ref={calendarStripRef}
      style={{ flex: 1, overflow: 'auto', position: 'relative' }}
    >
      {/* Sticky Header Container */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'white',
        borderBottom: '2px solid #ccc',
        minWidth: visibleWeeks * weekWidth,
        display: 'flex',
        flexDirection: 'row'
      }}>
        {weeksData.map((week, index) => {
          // Berechne die Tage der Woche (Montag bis Sonntag)
          const dayWidth = weekWidth / 7;
          const days = [];
          for (let d = 0; d < 7; d++) {
            const dayDate = new Date(week.fullDate);
            dayDate.setDate(dayDate.getDate() + d);
            const dayOfWeek = dayDate.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Samstag oder Sonntag
            
            days.push({
              date: dayDate,
              isWeekend: isWeekend,
              dayOfWeek: dayOfWeek
            });
          }

          return (
            <div
              key={`sticky-${index}`}
              style={{
                width: weekWidth,
                minWidth: weekWidth,
                maxWidth: weekWidth,
                backgroundImage: `linear-gradient(to right, transparent calc(100% - 1px), rgba(204, 204, 204, 0.5) calc(100% - 1px))`,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Wocheninformation */}
              <div style={{
                padding: '8px 8px 4px 8px',
                textAlign: 'center',
                backgroundColor: index % 2 === 0 ? '#BBDEFB' : '#f1f5fe',
                color: index % 2 === 0 ? '#333' : '#555',
                fontWeight: 'bold',
                fontSize: '11px',
                lineHeight: 1.2,
                position: 'relative'
              }}>
                <div>KW {week.weekNumber}</div>
                <div style={{ fontSize: '12px', marginTop: '2px' }}>
                  {week.mondayDate}
                </div>
              </div>
              {/* Tagesaufteilung im Header */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'row',
                height: '24px'
              }}>
                {days.map((day, dayIndex) => {
                  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
                  const dayName = dayNames[day.dayOfWeek];
                  const specialTime = getSpecialTimeForDate(day.date);
                  
                  return (
                    <div
                      key={dayIndex}
                      style={{
                        width: dayWidth,
                        minWidth: dayWidth,
                        height: '100%',
                        backgroundImage: `linear-gradient(to right, transparent calc(100% - 1px), rgba(204, 204, 204, 0.5) calc(100% - 1px))`,
                        backgroundColor: getHeaderBackgroundColorForDay(day.date, day.isWeekend, index),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '9px',
                        fontWeight: day.isWeekend ? 'bold' : 'normal',
                        color: day.isWeekend ? '#2d5233' : (index % 2 === 0 ? '#333' : '#555')
                      }}
                      title={specialTime ? specialTime.name : ''}
                    >
                      {dayName}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Container mit horizontalem Scrolling */}
      <div style={{ 
        minWidth: visibleWeeks * weekWidth, // Mindestbreite für horizontales Scrollen
        position: 'relative',
        height: 'calc(100% - 70px)' // Reduziert um die Höhe des sticky headers
      }}>
        {/* Hintergrund-Spalten */}
        <div 
          ref={backgroundRef}
          style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            height: contentHeight,
            display: 'flex',
            zIndex: 0,
            pointerEvents: 'none', // Verhindert Interferenz mit dem Inhalt
        minWidth: visibleWeeks * weekWidth,
            minHeight: '100vh' // Mindesthöhe für kleine Inhalte
          }}
        >
          {weeksData.map((week, index) => {
            // Berechne die Tage der Woche (Montag bis Sonntag)
            const dayWidth = weekWidth / 7;
            const days = [];
            for (let d = 0; d < 7; d++) {
              const dayDate = new Date(week.fullDate);
              dayDate.setDate(dayDate.getDate() + d);
              const dayOfWeek = dayDate.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Samstag oder Sonntag
              
              days.push({
                date: dayDate,
                isWeekend: isWeekend,
                dayOfWeek: dayOfWeek
              });
            }

            return (
              <div
                key={index}
                style={{
                  width: weekWidth, // Feste Breite pro Woche
                  minWidth: weekWidth,
                  maxWidth: weekWidth,
                  backgroundImage: `linear-gradient(to right, transparent calc(100% - 1px), rgba(204, 204, 204, 0.3) calc(100% - 1px))`,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                {/* Tagesaufteilung für Wochenenden - nur Hintergrund, ohne Header */}
                <div style={{ 
                  flex: 1, 
                  display: 'flex',
                  flexDirection: 'row',
                  minHeight: 'calc(100vh - 70px)' // Garantiere Mindesthöhe
                }}>
                  {days.map((day, dayIndex) => {
                    const specialTime = getSpecialTimeForDate(day.date);
                    
                    return (
                      <div
                        key={dayIndex}
                        style={{
                          width: dayWidth,
                          minWidth: dayWidth,
                          height: '100%',
                          backgroundImage: `linear-gradient(to right, transparent calc(100% - 1px), rgba(204, 204, 204, 0.3) calc(100% - 1px))`,
                          backgroundColor: getBackgroundColorForDay(day.date, day.isWeekend, index),
                          position: 'relative'
                        }}
                        title={specialTime ? `${specialTime.name} (${specialTime.start_date} - ${specialTime.end_date})` : ''}
                      >
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Hauptinhalt über den Hintergrund-Spalten */}
        <div 
          ref={contentRef}
          style={{ 
            position: 'relative', 
            zIndex: 1, 
            minHeight: '100%',
            padding: 0,
            minWidth: visibleWeeks * weekWidth
          }}
        >
          <PanelGroupsContainer 
            isAuthor={true} 
            onDataChange={handleContainerDataChange} 
            ref={panelGroupsContainerRef}
            visibleWeeks={visibleWeeks}
            weekWidth={weekWidth}
            isVisibleWeeksChanging={isVisibleWeeksChanging}
            isZoomChanging={isZoomChanging}
            onInfoPanelsChange={onInfoPanelsChange}
            batchOperations={batchOperations}
            onCheckForDuplicates={onCheckForDuplicates}
          />
        </div>
      </div>

      {/* Minimap-Overlay */}
      <Minimap
        groups={minimapGroups}
        visibleWeeks={visibleWeeks}
        weekWidth={weekWidth}
        scrollContainerRef={calendarStripRef}
        contentRef={contentRef}
      />
    </div>
  );
});

export default CalendarStrips;
